import { CheckCircleFilled, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, QRCode, Space, Steps, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { useNotify } from '@/api/notify';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { Notice } from '@/components/PageHeading';
import { formatDate } from '@core/format/dates';
import type {
  TwoFactorEnrolment as Enrolment,
  TwoFactorRecoveryCodes as RecoveryCodes,
  TwoFactorStatus,
} from '@core/session/twoFactor';
import type { SignedInUser } from '@core/session/types';
import { fontMono, palette, radius } from '@/theme';

/**
 * Two-factor setup for the signed-in user.
 *
 * Rendered outside `AppShell`, like the change-password screen and for a related reason: it is not
 * a menu destination. The menu is a projection of permissions, and securing your own sign-in is
 * not permissioned — a user holding no grants at all still has an account worth protecting. It is
 * reached from the account dropdown instead.
 *
 * The QR code is drawn in the browser from the `otpauth://` URI. A server-rendered image would put
 * the secret in a URL, and therefore in a proxy log, a browser history and an image cache — three
 * places a shared credential should never be.
 */
export function TwoFactorPage({ user, onClose }: { user: SignedInUser; onClose: () => void }) {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const notify = useNotify();

  const load = async () => {
    try {
      setStatus(await apiFetch<TwoFactorStatus>('/api/auth/two-factor'));
    } catch (caught) {
      setError(caught);
    }
  };

  // Once, on mount. `load` is deliberately absent from the dependencies: it is recreated every
  // render, so listing it would re-fetch forever.
  useEffect(() => {
    void load();
  }, []);

  /**
   * Runs a call, showing its failure in one place rather than at four call sites.
   *
   * A toast, unlike the load failure above: these are things the user just pressed, and the card
   * they pressed it on is still on screen behind the message. `load` keeps its inline alert because
   * a page with no status to render has nowhere to put one and would otherwise be blank.
   */
  const run = async (action: () => Promise<void>) => {
    setBusy(true);

    try {
      await action();
    } catch (caught) {
      notify.error(caught);
    } finally {
      setBusy(false);
    }
  };

  const begin = (currentPassword: string) =>
    run(async () => {
      setEnrolment(
        await apiFetch<Enrolment>('/api/auth/two-factor/enrol', {
          method: 'POST',
          body: { currentPassword },
        }),
      );
    });

  const confirm = (code: string) =>
    run(async () => {
      const issued = await apiFetch<RecoveryCodes>('/api/auth/two-factor/confirm', {
        method: 'POST',
        body: { code },
      });

      setCodes(issued.codes);
      setEnrolment(null);
      await load();

      notify.success('Two-factor authentication is on.');
    });

  const disable = (currentPassword: string) =>
    run(async () => {
      await apiFetch('/api/auth/two-factor/disable', {
        method: 'POST',
        body: { currentPassword },
      });

      setCodes(null);
      await load();

      notify.success('Two-factor authentication is off.');
    });

  const regenerate = (currentPassword: string) =>
    run(async () => {
      const issued = await apiFetch<RecoveryCodes>('/api/auth/two-factor/recovery-codes', {
        method: 'POST',
        body: { currentPassword },
      });

      setCodes(issued.codes);
      await load();
    });

  return (
    <div style={{ minHeight: '100vh', background: palette.canvas, padding: 24 }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography.Title level={4} style={{ margin: 0, fontWeight: 700 }}>
              <SafetyCertificateOutlined style={{ color: palette.teal, marginInlineEnd: 8 }} />
              Two-factor authentication
            </Typography.Title>

            <Button onClick={onClose}>Back to app</Button>
          </div>

          {error !== null && <ApiErrorAlert error={error} />}

          {/* Shown once, and immediately: a batch the user navigates away from is gone for good,
              so it has to be the first thing on the screen rather than below the fold. */}
          {codes !== null && <RecoveryCodeList codes={codes} onDismiss={() => setCodes(null)} />}

          {status?.enabled === true && (
            <EnabledCard
              status={status}
              busy={busy}
              onDisable={(password) => void disable(password)}
              onRegenerate={(password) => void regenerate(password)}
            />
          )}

          {status !== null && !status.enabled && enrolment === null && (
            <StartCard
              pending={status.enrolmentPending}
              busy={busy}
              onBegin={(password) => void begin(password)}
            />
          )}

          {enrolment !== null && (
            <ScanCard
              user={user}
              enrolment={enrolment}
              busy={busy}
              onConfirm={(code) => void confirm(code)}
              onCancel={() => setEnrolment(null)}
            />
          )}
        </Space>
      </div>
    </div>
  );
}

/**
 * The state an account is in before anything has been set up, and the one thing it needs from the
 * user to move on: their password.
 *
 * The password is asked for here rather than at the end because this is the moment that matters.
 * Anyone sitting at an unlocked, already-signed-in machine could otherwise enrol a phone of their
 * own and lock the real owner out — which would turn a security feature into an attack.
 */
function StartCard({
  pending,
  busy,
  onBegin,
}: {
  pending: boolean;
  busy: boolean;
  onBegin: (password: string) => void;
}) {
  return (
    <Card>
      <Typography.Paragraph>
        Your account signs in with a password alone. Adding a code from an authenticator app means a
        stolen or guessed password is not enough on its own.
      </Typography.Paragraph>

      {pending && (
        <div className="mb-4">
          <Notice tone="info" title="A setup was started but never finished">
            Nothing changed — your account still signs in as it did. Starting again issues a new
            code to scan; any entry you already added to your app will stop matching, so remove it.
          </Notice>
        </div>
      )}

      <Form<{ currentPassword: string }>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => onBegin(values.currentPassword)}
      >
        <Form.Item
          label="Confirm your password"
          name="currentPassword"
          rules={[{ required: true, message: 'Enter your current password.' }]}
        >
          <Input.Password size="large" autoComplete="current-password" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={busy} icon={<LockOutlined />}>
          Set up two-factor authentication
        </Button>
      </Form>
    </Card>
  );
}

/** The scan-and-confirm step. Nothing is switched on until a code from the app comes back. */
function ScanCard({
  user,
  enrolment,
  busy,
  onConfirm,
  onCancel,
}: {
  user: SignedInUser;
  enrolment: Enrolment;
  busy: boolean;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}) {
  return (
    <Card>
      <Steps
        size="small"
        current={1}
        style={{ marginBottom: 24 }}
        items={[{ title: 'Confirm password' }, { title: 'Scan' }, { title: 'Verify' }]}
      />

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <QRCode value={enrolment.uri} size={168} bordered={false} />

        <div style={{ flex: '1 1 260px', minWidth: 240 }}>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            Scan this with Google Authenticator, Microsoft Authenticator, Authy or any app that
            reads authenticator QR codes.
          </Typography.Paragraph>

          {/* Manual entry is not a fallback here, it is the normal case: the shop's computer has
              no camera pointed at it, and the phone doing the scanning is in someone's pocket. */}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
            Or add it by hand, as {user.username}@{user.tenantCode}:
          </Typography.Paragraph>

          <Typography.Paragraph
            copyable={{ text: enrolment.secret }}
            style={{
              fontFamily: fontMono,
              fontSize: 13,
              wordBreak: 'break-all',
              background: palette.canvas,
              border: `1px solid ${palette.rule}`,
              borderRadius: radius,
              padding: '8px 10px',
            }}
          >
            {enrolment.secret}
          </Typography.Paragraph>
        </div>
      </div>

      <Form<{ code: string }>
        layout="vertical"
        requiredMark={false}
        style={{ marginTop: 8 }}
        onFinish={(values) => onConfirm(values.code)}
      >
        <Form.Item
          label="Enter the code your app shows"
          name="code"
          rules={[{ required: true, message: 'Enter the six-digit code.' }]}
        >
          <Input
            size="large"
            autoFocus
            autoComplete="one-time-code"
            placeholder="123456"
            style={{ fontFamily: fontMono, letterSpacing: '0.08em', maxWidth: 200 }}
          />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={busy}>
            Turn on
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form>
    </Card>
  );
}

/** What the screen shows once 2FA is on: the state, the codes left, and the two ways out. */
function EnabledCard({
  status,
  busy,
  onDisable,
  onRegenerate,
}: {
  status: TwoFactorStatus;
  busy: boolean;
  onDisable: (password: string) => void;
  onRegenerate: (password: string) => void;
}) {
  const [intent, setIntent] = useState<'disable' | 'regenerate' | null>(null);

  return (
    <Card>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Tag color="green" icon={<CheckCircleFilled />}>
            On
          </Tag>
          <Typography.Text type="secondary">
            {status.enrolledOn === null
              ? 'Your sign-in asks for a code.'
              : // `formatDate`, not a bare `toLocaleDateString()` — that took the *browser's* default
                // locale, so this one line could read 8/4/2026 while every other date in the product
                // read 04/08/2026.
                `On since ${formatDate(status.enrolledOn)}.`}
          </Typography.Text>
        </div>

        {/* Running out is silent otherwise — the count only matters on the day the phone breaks,
            which is the day it is too late to generate more. */}
        {status.remainingRecoveryCodes <= 2 && (
          <Notice
            tone="warning"
            title={`${status.remainingRecoveryCodes} recovery ${
              status.remainingRecoveryCodes === 1 ? 'code' : 'codes'
            } left`}
          >
            Generate a new set while you still have your phone. Without one, losing it means asking
            an administrator to switch two-factor off for you.
          </Notice>
        )}

        <Typography.Text type="secondary">
          {status.remainingRecoveryCodes} unused recovery{' '}
          {status.remainingRecoveryCodes === 1 ? 'code' : 'codes'}.
        </Typography.Text>

        {intent === null ? (
          <Space>
            <Button onClick={() => setIntent('regenerate')}>New recovery codes</Button>
            <Button danger onClick={() => setIntent('disable')}>
              Turn off
            </Button>
          </Space>
        ) : (
          <Form<{ currentPassword: string }>
            layout="vertical"
            requiredMark={false}
            onFinish={(values) =>
              intent === 'disable'
                ? onDisable(values.currentPassword)
                : onRegenerate(values.currentPassword)
            }
          >
            {intent === 'disable' && (
              <div className="mb-4">
                <Notice tone="warning" title="Your account will sign in with a password alone">
                  Your current recovery codes stop working too. You can set two-factor up again at
                  any time.
                </Notice>
              </div>
            )}

            <Form.Item
              label="Confirm your password"
              name="currentPassword"
              rules={[{ required: true, message: 'Enter your current password.' }]}
            >
              <Input.Password size="large" autoComplete="current-password" autoFocus />
            </Form.Item>

            <Space>
              <Button type="primary" danger={intent === 'disable'} htmlType="submit" loading={busy}>
                {intent === 'disable' ? 'Turn off' : 'Generate codes'}
              </Button>
              <Button onClick={() => setIntent(null)}>Cancel</Button>
            </Space>
          </Form>
        )}
      </Space>
    </Card>
  );
}

/**
 * The one and only showing of a batch of recovery codes.
 *
 * The server keeps hashes, so this is genuinely the last time these values exist anywhere the user
 * can reach. That is stated rather than implied, and the copy action is the prominent one — a shop
 * that pastes these into the file it already keeps is better off than one that means to write them
 * down later.
 */
function RecoveryCodeList({ codes, onDismiss }: { codes: string[]; onDismiss: () => void }) {
  const notify = useNotify();

  return (
    <Card>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Save your recovery codes
      </Typography.Title>

      <Typography.Paragraph type="secondary">
        Each one signs you in once if you lose your phone. This is the only time they are shown —
        leave this screen and you will need to generate a new set. Keep them somewhere other than
        the phone holding your authenticator app.
      </Typography.Paragraph>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 8,
          fontFamily: fontMono,
          background: palette.canvas,
          border: `1px solid ${palette.rule}`,
          borderRadius: radius,
          padding: 12,
          marginBottom: 16,
        }}
      >
        {codes.map((code) => (
          <span key={code}>{code}</span>
        ))}
      </div>

      <Space>
        <Button
          type="primary"
          onClick={() => {
            void navigator.clipboard?.writeText(codes.join('\n'));
            notify.success('Recovery codes copied.');
          }}
        >
          Copy all
        </Button>
        <Button onClick={onDismiss}>I have saved them</Button>
      </Space>
    </Card>
  );
}
